import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { DTF_PRINT_WIDTH } from "@/config/dtf";
import { formatNum, whatsappLink } from "@/lib/dtf";

const faqs = [
  {
    q: "A calculadora DTF têxtil é gratuita?",
    a: "Sim. O ClickDTF pode ser utilizado gratuitamente e não exige cadastro.",
  },
  {
    q: "Preciso criar uma conta?",
    a: "Não. A calculadora funciona sem login e sem cadastro.",
  },
  {
    q: "Em quais tecidos o DTF têxtil pode ser aplicado?",
    a: "O DTF têxtil funciona em algodão, poliéster, tecidos mistos, malha fria, dry fit, moletom, sarja e outros tecidos, sempre com aplicação por prensa térmica.",
  },
  {
    q: "Qual é a largura do DTF têxtil utilizada no cálculo?",
    a: `O cálculo utiliza como referência a largura fixa de ${formatNum(DTF_PRINT_WIDTH, 0)} cm do filme DTF têxtil. A cobrança é por metro linear: ${formatNum(DTF_PRINT_WIDTH, 0)} cm de largura por 100 cm de comprimento.`,
  },
  {
    q: "Como saber quantos metros de DTF têxtil vou precisar?",
    a: "Informe as dimensões da arte e a quantidade. A ferramenta encaixa as estampas lado a lado dentro da largura do filme, testa a rotação de 90° e mostra o comprimento linear realmente necessário.",
  },
  {
    q: "Posso encomendar o DTF têxtil depois de calcular?",
    a: "Sim. Após realizar o cálculo, você pode entrar em contato diretamente pelo WhatsApp, já com os dados da simulação na mensagem.",
  },
  {
    q: "O valor da calculadora é definitivo?",
    a: "Não. O resultado é uma estimativa. Condições comerciais, a montagem das artes no filme e características específicas do arquivo (cores, tamanho real, margens) podem alterar o orçamento final.",
  },
];

export const Faq = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="duvidas" className="scroll-mt-20 bg-[#f6f6f5] py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
          <Reveal>
            <p className="num text-[11px] uppercase tracking-[0.28em] text-[#f58244]">
              Dúvidas
            </p>
            <h2 className="mt-3 text-3xl font-extrabold leading-tight text-black sm:text-4xl">
              Perguntas frequentes
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-neutral-600">
              Ainda com dúvidas? Fale com nossa equipe pelo WhatsApp.
            </p>
            <a
              href={whatsappLink("Olá! Tenho uma dúvida sobre impressão DTF têxtil.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="faq-cta-whatsapp"
              className="link-line mt-4 inline-block text-sm font-semibold text-[#f58244]"
            >
              Tirar minha dúvida
            </a>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="divide-y divide-neutral-200 border-y border-neutral-200">
              {faqs.map((f, i) => {
                const isOpen = open === i;
                return (
                  <article key={f.q}>
                    <h3>
                      <button
                        type="button"
                        data-testid={`faq-item-${i + 1}`}
                        aria-expanded={isOpen}
                        onClick={() => setOpen(isOpen ? -1 : i)}
                        className="flex w-full items-center justify-between gap-5 py-5 text-left"
                      >
                        <span className="text-base font-semibold text-black sm:text-lg">
                          {f.q}
                        </span>
                        <motion.span
                          animate={{ rotate: isOpen ? 45 : 0 }}
                          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
                            isOpen
                              ? "border-[#f58244] bg-[#f58244] text-white"
                              : "border-neutral-300 text-black"
                          }`}
                        >
                          <Plus className="h-4 w-4" strokeWidth={2.6} />
                        </motion.span>
                      </button>
                    </h3>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          data-testid={`faq-answer-${i + 1}`}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <p className="max-w-2xl pb-6 text-sm leading-relaxed text-neutral-600 sm:text-base">
                            {f.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </article>
                );
              })}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
